if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.onExternalOptionChange = this.handleExternalOptionChange.bind(this);
        this.addEventListener('change', this.onExternalOptionChange);
        this.onLensUnavailableBlock = this.handleLensUnavailableBlock.bind(this);
        this.addEventListener('click', this.onLensUnavailableBlock, true);

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        window.setTimeout(() => {
          this.syncProductFormAvailability();
        }, 400);
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.removeEventListener('change', this.onExternalOptionChange);
        this.removeEventListener('click', this.onLensUnavailableBlock, true);
      }

      handleLensUnavailableBlock(event) {
        if (!this.classList.contains('ciw-pdp--lens-unavailable')) return;

        const lensTarget = event.target.closest(
          'lensadvizor-btn, #la-select-lenses-btn, .la-select-lenses-btn, [class*="la-select-lenses"]'
        );
        if (!lensTarget || !this.contains(lensTarget)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      }

      handleExternalOptionChange(event) {
        if (!event.target.matches('.ciw-pdp__variant-fieldset--frame-size input[type="radio"]')) return;

        const variant = this.resolveSelectedVariant();
        this.syncProductFormAvailability(variant);

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target: event.target,
            selectedOptionValues: this.getAllSelectedOptionValueIds(),
          },
        });
      }

      getAllSelectedOptionValueIds() {
        return Array.from(
          this.querySelectorAll(
            'variant-selects fieldset input[type="radio"]:checked, .ciw-pdp__variant-fieldset input[type="radio"]:checked'
          )
        )
          .map((input) => input.dataset.optionValueId)
          .filter(Boolean);
      }

      // Trim radio name before parsing option position (Liquid capture used to append \\n).
      findVariantBySelectedOptions(root = this) {
        const variants = this.getProductVariants();
        const selectedByPosition = {};
        const picker = root.querySelector('variant-selects') || root;

        picker.querySelectorAll(
          'fieldset input[type="radio"]:checked, .ciw-pdp__variant-fieldset input[type="radio"]:checked'
        ).forEach((input) => {
          const positionMatch = String(input.name || '')
            .trim()
            .match(/-(\d+)$/);
          if (positionMatch) {
            selectedByPosition[parseInt(positionMatch[1], 10)] = input.value;
          }
        });

        picker.querySelectorAll('select').forEach((select) => {
          const selectedOption = select.selectedOptions[0];
          if (!selectedOption) return;

          const selectedValue = String(selectedOption.value || '').trim();
          if (!selectedValue) return;

          for (let i = 1; i <= 3; i += 1) {
            const hasValue = variants.some(
              (entry) => String(entry[`option${i}`] || '').trim() === selectedValue
            );
            if (hasValue) {
              selectedByPosition[i] = selectedOption.value;
              break;
            }
          }
        });

        const positions = Object.keys(selectedByPosition);
        if (!positions.length || !variants.length) return null;

        return (
          variants.find((variant) =>
            positions.every(
              (position) =>
                String(variant[`option${position}`] || '').trim() ===
                String(selectedByPosition[position] || '').trim()
            )
          ) || null
        );
      }

      getVariantById(variantId) {
        if (!variantId) return null;
        return (
          this.getProductVariants().find((entry) => String(entry.id) === String(variantId)) || null
        );
      }

      getVariantFromFormInput() {
        const input = this.querySelector(
          `#product-form-${this.dataset.section} input[name="id"], product-form input[name="id"]`
        );
        return this.getVariantById(input?.value);
      }

      resolveSelectedVariant(context = null) {
        const contextNode =
          context && typeof context.querySelector === 'function'
            ? context.querySelector('product-info') || context
            : null;
        const isFetchedContext = contextNode && contextNode !== this;

        if (isFetchedContext) {
          return (
            this.getSelectedVariant(contextNode) ||
            this.findVariantBySelectedOptions(contextNode) ||
            this.findVariantBySelectedOptions() ||
            this.getVariantFromFormInput()
          );
        }

        return (
          this.findVariantBySelectedOptions() ||
          this.getVariantFromFormInput() ||
          this.getSelectedVariant(this)
        );
      }

      isSelectedColorUnavailable() {
        const colorInput = this.querySelector(
          '.ciw-pdp__variant-fieldset--frame-color input[type="radio"]:checked, variant-selects .ciw-pdp__variant-fieldset--frame-color input[type="radio"]:checked'
        );
        if (!colorInput) return false;

        return (
          colorInput.classList.contains('visually-disabled') || colorInput.dataset.colorAvailable === 'false'
        );
      }

      relaxColorSoldOutIfAnotherSizeFits() {
        const colorInput = this.querySelector(
          '.ciw-pdp__variant-fieldset--frame-color input[type="radio"]:checked, variant-selects .ciw-pdp__variant-fieldset--frame-color input[type="radio"]:checked'
        );
        if (!colorInput) return;
        const marked =
          colorInput.classList.contains('visually-disabled') || colorInput.dataset.colorAvailable === 'false';
        if (!marked) return;

        const variant = this.findVariantBySelectedOptions();
        const colorName = String(colorInput.value || '').trim();
        if (!variant || variant.available !== true) return;
        if (this.getVariantColorKey(variant) !== colorName) return;

        colorInput.classList.remove('visually-disabled');
        colorInput.dataset.colorAvailable = 'true';
        const soldOutEl = colorInput
          .closest('.ciw-pdp__variant-fieldset, fieldset')
          ?.querySelector('[data-color-sold-out]');
        if (soldOutEl) soldOutEl.hidden = true;
      }

      isVariantSoldOut(variant) {
        if (this.isSelectedColorUnavailable()) return true;
        return Boolean(variant && variant.available === false);
      }

      syncProductFormAvailability(variantHint) {
        this.relaxColorSoldOutIfAnotherSizeFits();
        const variant = variantHint || this.resolveSelectedVariant();
        this.updateVariantInputs(variant?.id);
        this.syncChooseLensesSlot(variant);

        if (!this.productForm) return;

        const submitLabel = this.productForm.dataset.submitLabel;

        if (!variant) {
          this.productForm.toggleSubmitButton(true, window.variantStrings.unavailable);
          return;
        }

        if (this.isVariantSoldOut(variant)) {
          this.productForm.toggleSubmitButton(true, window.variantStrings.soldOut);
        } else {
          this.productForm.toggleSubmitButton(false, submitLabel);
        }
      }

      syncChooseLensesSlot(variant) {
        const slot = this.querySelector(`#CiwChooseLensesSlot-${this.dataset.section}`);
        if (!slot) return;

        const showUnavailable = this.isVariantSoldOut(variant);
        const unavailableBtn = slot.querySelector('.ciw-pdp__choose-lenses-unavailable');
        const lensButtons = this.querySelectorAll(
          'lensadvizor-btn, #la-select-lenses-btn, .la-select-lenses-btn, [class*="la-select-lenses"]'
        );

        this.classList.toggle('ciw-pdp--lens-unavailable', showUnavailable);
        slot.classList.toggle('ciw-pdp__choose-lenses-slot--show-unavailable', showUnavailable);
        slot.classList.toggle(
          'ciw-pdp__choose-lenses-slot--has-lens',
          Boolean(lensButtons.length && !showUnavailable)
        );

        if (unavailableBtn) {
          unavailableBtn.hidden = !showUnavailable;
        }

        lensButtons.forEach((lensBtn) => {
          lensBtn.hidden = showUnavailable;
          lensBtn.setAttribute('aria-hidden', showUnavailable ? 'true' : 'false');
          if (showUnavailable) {
            lensBtn.setAttribute('tabindex', '-1');
          } else {
            lensBtn.removeAttribute('tabindex');
          }
        });
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      syncGalleryForVariant(variant) {
        if (!variant?.id || !this.shouldUpdateVariantMedia(variant)) return;

        const run = () => {
          const resolved =
            this.resolveSelectedVariant()?.id ||
            variant?.id ||
            document.querySelector(`#product-form-${this.dataset.section} input[name="id"]`)?.value;
          if (!resolved) return;
          window.ciwVariantMediaHandler?.updateAdditionalMediaFromCurrentPage?.(
            String(resolved),
            this.sectionId
          );
        };

        if (window.ciwVariantMediaHandler) {
          run();
        } else {
          requestAnimationFrame(run);
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const optionValues = this.classList.contains('ciw-pdp')
          ? this.getAllSelectedOptionValueIds()
          : selectedOptionValues;
        const variant = this.resolveSelectedVariant();
        this.syncProductFormAvailability(variant);
        this.syncGalleryForVariant(variant);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, optionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getProductVariants() {
        const variantsScript = this.querySelector('script[type="application/json"][id^="ProductJSON"]');
        if (!variantsScript) return [];

        try {
          const parsed = JSON.parse(variantsScript.textContent) || [];
          return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
          return [];
        }
      }

      isSizeLikeOptionValue(value) {
        return /^(xs|s|small|medium|large|xl|xxl|\d+\s*-|\d+mm)/i.test(String(value || '').trim());
      }

      getVariantColorKey(variant) {
        if (!variant) return '';

        if (variant.option1 != null && variant.option2 != null) {
          if (this.isSizeLikeOptionValue(variant.option1)) return String(variant.option2);
          if (this.isSizeLikeOptionValue(variant.option2)) return String(variant.option1);
        }

        const parts = String(variant.title || '').split(' / ');
        return parts.length > 1 ? parts.slice(1).join(' / ') : String(variant.title || '');
      }

      galleryFiltersByVariant(root = document) {
        const gallery = root.getElementById(`MediaGallery-${this.sectionId}`);
        return gallery?.dataset.galleryFilter === 'variant';
      }

      shouldUpdateVariantMedia(variant) {
        const colorKey = this.getVariantColorKey(variant);
        const previousColorKey = this.dataset.ciwColorKey || '';

        if (colorKey) this.dataset.ciwColorKey = colorKey;
        if (!previousColorKey || !colorKey) return true;

        return previousColorKey !== colorKey;
      }

      resolveFeaturedMediaId(html, variant) {
        const root = html || document;
        const mapEl = root.getElementById(`CiwVariantMediaMap-${this.sectionId}`);
        const colorKey = this.getVariantColorKey(variant);
        const variants = this.getProductVariants();
        const filterByVariant = this.galleryFiltersByVariant(root);

        if (mapEl) {
          try {
            const data = JSON.parse(mapEl.textContent);

            const currentEntry = (data.variants || []).find(
              (entry) => String(entry.id) === String(variant?.id)
            );
            if (currentEntry?.featuredMediaId) {
              return currentEntry.featuredMediaId;
            }
            if (currentEntry?.frontMediaId) {
              return currentEntry.frontMediaId;
            }

            if (!filterByVariant && colorKey) {
              for (const entry of data.variants || []) {
                const record = variants.find((item) => String(item.id) === String(entry.id));
                if (record && this.getVariantColorKey(record) === colorKey && entry.frontMediaId) {
                  return entry.frontMediaId;
                }
              }
            }
          } catch (error) {
            /* ignore malformed map */
          }
        }

        if (!filterByVariant) {
          for (const entry of variants) {
            if (this.getVariantColorKey(entry) === colorKey && entry.featured_media?.id) {
              return entry.featured_media.id;
            }
          }
        }

        return variant?.featured_media?.id;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.resolveSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.syncProductFormAvailability(variant);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (this.shouldUpdateVariantMedia(variant)) {
            if (this.galleryFiltersByVariant(html)) {
              this.syncGalleryForVariant(variant);
            } else {
              this.updateMedia(html, this.resolveFeaturedMediaId(html, variant));
            }
          } else if (window.ciwVariantMediaHandler) {
            const variantId = String(variant?.id || '');
            if (variantId) {
              window.ciwVariantMediaHandler.lastGalleryColorKey =
                this.getVariantColorKey(variant);
            }
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('first30-badge', (source) => source.classList.contains('hidden'));
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        const variant = this.resolveSelectedVariant();
        if (variant) {
          this.syncProductFormAvailability(variant);
          return;
        }

        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
